import { useRef, useState } from 'react';

import { useFeatureToggle, useListState } from 'hooks';
import type { GTMItemListId, GTMItemListName } from 'hooks/gtm';
import type { BasketRowResponse, CartError } from 'models/api';
import type { VariantId } from 'models/product';
import type { Branded, CustomizationId } from 'types';
import { cn } from 'utils/classNames';
import { goToElement } from 'utils/dom';
import { afterNextPaint } from 'utils/helpers';

import BasketRow, {
	type BasketRowId,
	getBasketRowId,
	type SubtitleVariant,
} from './BasketRow';
import RemovedBasketRow from './RemovedBasketRow';

type RemovedRowId = Branded<string, 'RemovedRowId'>;
type ListItemId = BasketRowId | RemovedRowId;

export interface RemovedRow {
	/**
	 * Id of the item this message followed when the row was
	 * removed, or undefined if it was the first row in the list.
	 */
	afterRowId: ListItemId | undefined;
	customizationId: CustomizationId | undefined;
	gtmItemListId: GTMItemListId | undefined;
	gtmItemListName: GTMItemListName | undefined;
	quantity: number;
	status: 'removed' | 'restored';
	title: string | undefined;
	variantId: VariantId;
}

type ListItem =
	| { type: 'message'; row: RemovedRow }
	| { type: 'product'; row: BasketRowResponse };

// Includes customizationId just like getBasketRowId to handle the same variant
// with/without/with different customizations.
function getRemovedRowId(
	row: Pick<RemovedRow, 'customizationId' | 'variantId'>,
) {
	return [row.variantId, row.customizationId]
		.filter(Boolean)
		.join('-') as RemovedRowId;
}

function getListItemId(item: ListItem) {
	return item.type === 'product'
		? getBasketRowId(item.row)
		: getRemovedRowId(item.row);
}

export interface Props {
	className?: string;
	errors?: CartError[];
	hasShelfInformation?: boolean;
	hasStoreStock?: boolean;
	moveToCartButtonText?: string;
	moveToWishlistButtonText?: string;
	onMoveToCart?: (row: BasketRowResponse) => Promise<void>;
	onMoveToWishlist?: (row: BasketRowResponse) => Promise<void>;
	onPickedChange?: (row: BasketRowResponse) => Promise<void>;
	onQuantityChange?: (row: BasketRowResponse, qty: number) => Promise<void>;
	onRemove?: (row: BasketRowResponse) => Promise<void>;
	onRemovedRestore?: (removedRow: RemovedRow) => Promise<void>;
	pickedCheckboxLabel?: string;
	removeButtonText?: string;
	removeConfirmationText?: string;
	removedMessageText?: string;
	restoreButtonText?: string;
	restoredMessageText?: string;
	rows: BasketRowResponse[];
	subtitleVariant: SubtitleVariant;
}

/**
 * Common list of basket rows for cart and wishlist.
 *
 * Handles rendering and positioning of removed items for undo functionality.
 */
export default function Basket({
	className,
	errors,
	hasShelfInformation,
	hasStoreStock,
	moveToCartButtonText,
	moveToWishlistButtonText,
	onMoveToCart,
	onMoveToWishlist,
	onPickedChange,
	onQuantityChange,
	onRemove,
	onRemovedRestore,
	pickedCheckboxLabel,
	removeButtonText,
	removeConfirmationText,
	removedMessageText,
	restoreButtonText,
	restoredMessageText,
	rows,
	subtitleVariant,
}: Props) {
	const { onlineCommerceEnabled, wishlistEnabled } = useFeatureToggle();
	const [removedVariants, setRemovedVariants] = useState<RemovedRow[]>([]);

	const allRowsRef = useRef<(HTMLElement | null)[]>([]);
	const removedTextsRef = useRef<
		Map<RemovedRowId, HTMLParagraphElement | null>
	>(new Map());
	const focusRemovedText = (rowId: RemovedRowId) => {
		afterNextPaint(() => {
			removedTextsRef.current.get(rowId)?.focus();
		});
	};

	const setRemovedVariantStatus = (
		rowId: RemovedRowId,
		status: RemovedRow['status'],
	) => {
		setRemovedVariants((prev) =>
			prev.map((removed) =>
				getRemovedRowId(removed) === rowId ? { ...removed, status } : removed,
			),
		);
	};

	const {
		addItems: addInvalidQuantityId,
		items: invalidQuantityIds,
		removeItems: removeInvalidQuantityId,
	} = useListState<VariantId>();

	const handleQuantityChange = async (
		row: BasketRowResponse,
		qty: number | undefined,
	) => {
		if (!qty) {
			return;
		}
		removeInvalidQuantityId(row.variantId);
		try {
			// Looks possibly undefined here but it's checked in the JSX.
			await onQuantityChange?.(row, qty);
		} catch {
			addInvalidQuantityId(row.variantId);
		}
	};

	const items: ListItem[] = rows.map((row) => ({ type: 'product', row }));
	// Each message is placed after the item its variant followed when removed,
	// which can be the message of another removed variant. Since removedVariants
	// is in removal order, that anchor is always placed first. A variant that
	// had no preceding item gets its message first in the list.
	for (const removed of removedVariants) {
		const anchorIndex = items.findIndex(
			(item) => getListItemId(item) === removed.afterRowId,
		);
		items.splice(anchorIndex + 1, 0, { type: 'message', row: removed });
	}

	const handleRemove = async (row: BasketRowResponse, index: number) => {
		await onRemove?.(row);
		const customizationId = row.customization?.customization?.id;
		const basketRowId = getBasketRowId(row);
		const removedRowId = getRemovedRowId({
			customizationId,
			variantId: row.variantId,
		});
		// Anchor a removed item to its preceding item. A restored
		// variant can follow its own message which will have the
		// same id, so go backwards from the row's place in the
		// list and find the first preceding item with a different
		// id to avoid anchoring to itself.
		const prevItem = items
			.slice(0, index)
			.findLast((itm) => getListItemId(itm) !== basketRowId);
		setRemovedVariants((prev: RemovedRow[]) => [
			// Filter out any existing entries for this row to not
			// stack multiple (i.e. remove -> restore -> remove etc.)
			...prev.filter((removed) => getRemovedRowId(removed) !== removedRowId),
			{
				afterRowId: prevItem ? getListItemId(prevItem) : undefined,
				customizationId,
				gtmItemListId: row.metaData?.gtmItemListId,
				gtmItemListName: row.metaData?.gtmItemListName,
				quantity: row.quantity,
				status: 'removed',
				title: row.title,
				variantId: row.variantId,
			},
		]);
		focusRemovedText(removedRowId);
	};

	const [isConfirmingLastRemove, setIsConfirmingLastRemove] = useState(false);
	const removeButtonRef = useRef<HTMLButtonElement>(null);
	const removeConfirmationTextRef = useRef<HTMLParagraphElement>(null);
	const handleRemoveLast = () => {
		setIsConfirmingLastRemove(true);
		afterNextPaint(() => {
			removeConfirmationTextRef.current?.focus();
		});
	};
	const handleRemoveConfirmCancel = () => {
		setIsConfirmingLastRemove(false);
		afterNextPaint(() => {
			removeButtonRef.current?.focus();
		});
	};

	return (
		<div
			className={cn(
				'divide-y divide-subtle border-y border-subtle *:py-8 print:*:py-4',
				className,
			)}
		>
			{items.map((item, index) => {
				if (item.type === 'message') {
					// Basically 'show if restore props are set', tricky to express with
					// proper types.
					if (
						!onRemovedRestore ||
						!removedMessageText ||
						!restoreButtonText ||
						!restoredMessageText
					) {
						return null;
					}
					const removedRow = item.row;
					const removedRowId = getRemovedRowId(removedRow);
					return (
						<RemovedBasketRow
							key={`removed-${removedRowId}`}
							ref={(node) => {
								allRowsRef.current[index] = node;
								if (node) {
									removedTextsRef.current.set(removedRowId, node);
								} else {
									removedTextsRef.current.delete(removedRowId);
								}
							}}
							removedMessageText={removedMessageText}
							restoreButtonText={restoreButtonText}
							restoredMessageText={restoredMessageText}
							status={removedRow.status}
							title={removedRow.title}
							onRestore={async () => {
								await onRemovedRestore(removedRow);
								setRemovedVariantStatus(removedRowId, 'restored');
								focusRemovedText(removedRowId);
							}}
							onMessageClose={() => {
								// Any messages anchored to this one must switch to this
								// one's anchor, i.e. the item before this one.
								setRemovedVariants((prev) =>
									prev
										.filter(
											(removed) => getRemovedRowId(removed) !== removedRowId,
										)
										.map((removed) =>
											removed.afterRowId === removedRowId
												? {
														...removed,
														afterRowId: removedRow.afterRowId,
													}
												: removed,
										),
								);
								// Focus on the next item, or previous if this is last.
								const target =
									allRowsRef.current[index + 1] ??
									allRowsRef.current[index - 1];
								if (target) {
									goToElement(target);
								}
							}}
						/>
					);
				}
				const { row } = item;
				const hasCustomization = Boolean(row.customization?.customization);
				return (
					<BasketRow
						key={getBasketRowId(row)}
						ref={(node) => {
							allRowsRef.current[index] = node;
						}}
						hasInvalidQuantity={invalidQuantityIds.includes(row.variantId)}
						hasShelfInformation={hasShelfInformation}
						hasStoreStock={hasStoreStock}
						moveToCartButtonText={moveToCartButtonText}
						moveToWishlistButtonText={moveToWishlistButtonText}
						pickedCheckboxLabel={pickedCheckboxLabel}
						removeButtonRef={removeButtonRef}
						removeButtonText={removeButtonText}
						removeConfirmationText={removeConfirmationText}
						removeConfirmationTextRef={removeConfirmationTextRef}
						row={row}
						subtitleVariant={subtitleVariant}
						onMoveToCart={
							onlineCommerceEnabled && row.isSellable && onMoveToCart
								? () => onMoveToCart(row)
								: undefined
						}
						onMoveToWishlist={
							wishlistEnabled && !hasCustomization && onMoveToWishlist
								? () => onMoveToWishlist(row)
								: undefined
						}
						onPickedChange={
							onPickedChange ? () => onPickedChange(row) : undefined
						}
						onQuantityChange={
							onQuantityChange
								? (qty) => handleQuantityChange(row, qty)
								: undefined
						}
						onRemove={
							onRemove
								? rows.length === 1
									? handleRemoveLast
									: () => handleRemove(row, index)
								: undefined
						}
						onRemoveConfirm={
							isConfirmingLastRemove
								? () => handleRemove(row, index)
								: undefined
						}
						onRemoveConfirmCancel={handleRemoveConfirmCancel}
						errors={errors?.filter(
							(error) =>
								error.key === row.variantId ||
								error.key === row.customization?.customization?.id,
						)}
					/>
				);
			})}
		</div>
	);
}
Basket.displayName = 'Basket';
